import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { LinearProgress, Stack, Typography } from "@mui/material";
import { take } from "lodash";
import type { FC } from "react";
import { useMemo } from "react";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import { useAgents } from "../agents/useAgents";
import { useAdminFiltersValue } from "../useAdminFilters";
import { CustomerNoteListItem } from "./CustomerNoteListItem";
import { useCustomerNotesAdmin } from "./useCustomerNotesAdmin";
import { useFilteredNotes } from "./useFilteredNotes";

const NoNotesPlaceholder = () => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: "center",
      color: "text.secondary",
      marginTop: 1,
    }}
  >
    <NoteAltIcon fontSize="small" />
    <Typography>Brak notatek dla wybranych filtrów</Typography>
  </Stack>
);

const CustomerNotesAdminPage: FC = () => {
  const { dateFrom, dateTo, accountManagerId, authorId, type } = useAdminFiltersValue("customer-notes");

  const { data: notes = [], isLoading: isNotesLoading } = useCustomerNotesAdmin({ dateFrom, dateTo });
  const { agents, isLoading: isAgentsLoading } = useAgents();
  const { limit, loadMore } = useLimit();

  const agentNameAndIdByLoginEmail = useMemo(() => {
    return new Map(
      agents.flatMap((a) => (a.loginEmail ? [[a.loginEmail.toLowerCase(), { name: a.name, id: a.id }] as const] : [])),
    );
  }, [agents]);

  const notesWithResolvedAuthor = useMemo(() => {
    return notes.map(({ author, ...other }) => {
      const { name, id } = agentNameAndIdByLoginEmail.get(author) ?? {};
      return {
        ...other,
        author: { email: author, name: name ?? author, id: id ?? "" },
      };
    });
  }, [agentNameAndIdByLoginEmail, notes]);

  const { filteredNotes, isLoading: areFiltersLoading } = useFilteredNotes({
    notes: notesWithResolvedAuthor,
    filters: { accountManagerId, authorId, type },
  });

  const isLoading = isNotesLoading || isAgentsLoading || areFiltersLoading;

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        height: "100%",
      }}
    >
      {isLoading && <LinearProgress />}
      {!isLoading && filteredNotes.length === 0 ? (
        <NoNotesPlaceholder />
      ) : (
        <InfiniteScroll
          dataLength={limit < filteredNotes.length ? limit : filteredNotes.length}
          fetchData={loadMore}
          hasMore={limit < filteredNotes.length}
        >
          <Stack spacing={1}>
            {take(filteredNotes, limit).map((note) => (
              <CustomerNoteListItem key={note.id} note={note} />
            ))}
          </Stack>
        </InfiniteScroll>
      )}
    </Stack>
  );
};

export default CustomerNotesAdminPage;
